'use client';

/**
 * ErrorBoundary - 전역 에러 경계 컴포넌트
 *
 * Description: React Error Boundary를 클래스 컴포넌트로 구현.
 *   - 렌더링 중 발생하는 예기치 않은 에러를 포착하여 앱 전체 크래시를 방지
 *   - 사용자 친화적인 한국어 에러 메시지 표시
 *   - 재시도 버튼으로 복구 시도 가능
 *   - 개발 모드에서만 상세 에러 정보 노출
 */

import React from 'react';
import Button from './Button';
import styles from './ErrorBoundary.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ErrorBoundaryProps {
  /** 보호할 자식 컴포넌트 */
  children: React.ReactNode;
  /**
   * 에러 발생 시 대신 렌더링할 커스텀 폴백 UI.
   * 제공하지 않으면 기본 에러 UI를 사용.
   */
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // 프로덕션에서는 외부 에러 추적 서비스(Sentry 등)로 전송 가능
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console -- intentional dev-only error logging for render crashes
      console.error('[ErrorBoundary] 렌더링 에러 발생:', error, info.componentStack);
    }
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // 커스텀 폴백이 제공된 경우 우선 사용
    if (this.props.fallback) {
      return this.props.fallback;
    }

    const isDev = process.env.NODE_ENV === 'development';

    return (
      <div className={styles.container} role="alert" aria-live="assertive">
        <div className={styles.card}>
          {/* 에러 아이콘 */}
          <div className={styles.iconWrap} aria-hidden="true">
            <svg
              className={styles.icon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>

          {/* 메시지 */}
          <h2 className={styles.title}>문제가 발생했습니다</h2>
          <p className={styles.description}>
            예기치 않은 오류가 발생했습니다. 잠시 후 다시 시도하거나, 문제가 지속되면 관리자에게
            문의해 주세요.
          </p>

          {/* 개발 모드 에러 상세 정보 */}
          {isDev && this.state.error && (
            <details className={styles.details}>
              <summary className={styles.detailsSummary}>에러 상세 정보 (개발 모드)</summary>
              <pre className={styles.errorMessage}>{this.state.error.message}</pre>
              {this.state.error.stack && (
                <pre className={styles.errorStack}>{this.state.error.stack}</pre>
              )}
            </details>
          )}

          {/* 액션 버튼 */}
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => window.location.reload()}>
              페이지 새로고침
            </Button>
            <Button onClick={this.handleRetry}>다시 시도</Button>
          </div>
        </div>
      </div>
    );
  }
}
